import IconPlusSmall from 'central-icons/IconPlusSmall';
import classNames from 'classnames';
import { useCallback, useState } from 'react';

import { TaxonomyType } from '@revelio/data-access';
import {
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useDisclosure,
} from '@revelio/ui';

import { useActivityTaxonomyConfig } from '../../../../shared/selection-lists/hooks/useActivityTaxonomyConfig';
import { useCurrentReferenceTaxonomyVersion } from '../../../../shared/selection-lists/hooks/useReferenceTaxonomyVersions';
import { useCurrentRoleTaxonomyVersion } from '../../../../shared/selection-lists/hooks/useRoleTaxonomyAdapter';
import { CardListSelect } from '../../../../shared/ui/card-list';
import { useReportFilters } from '../../../reports/hooks/useReportFilters';
import { FilterModal } from '../../filters/components/FilterModal';
import { useFilterContext } from '../../filters/contexts/FilterProvider';
import { getFilterDefinition } from '../../filters/engine/filterRegistry';
import { type Filter, FilterId, FilterType } from '../../filters/types/filters';
import { createNonPrimaryParameterFilterMap } from '../../filters/utils/createNonPrimaryParameterFilterMap';
import {
  useReportParameters,
  useTabContentConfig,
} from '../PageConfigProvider/PageConfigProvider';
import type {
  PrimaryFilterConfig,
  ReportParameterConfig,
} from '../PageConfigProvider/page-reducer';
import type {
  PlotParameter,
  PositionedItem,
} from '../PageConfigProvider/types/item';
import { EditParameterModal } from '../Reports/Parameters/EditParameterModal';
import { ParamItemMenu } from '../Reports/Parameters/ParamItemMenu';
import { toReadableFilterName } from '../Reports/Parameters/parameters';
import { useAddParameterFilterGroup } from '../Reports/Parameters/useAddParameterFilterGroup';
import styles from './report-parameter-selector.module.css';
import labelStyles from './section-label.module.css';

type ReportParameterSelectorProps = {
  parameters: PlotParameter[];
  primaryFilter: PrimaryFilterConfig;
  item: PositionedItem;
};

export const ReportParameterSelector = ({
  parameters,
  primaryFilter,
  item,
}: ReportParameterSelectorProps) => {
  const {
    activeFilterMap,
    filterState,
    dispatch: filterDispatch,
  } = useFilterContext();
  const { dispatch } = useTabContentConfig();
  const { version: roleTaxonomyVersion } = useCurrentRoleTaxonomyVersion();
  const { version: skillTaxonomyVersion } = useCurrentReferenceTaxonomyVersion({
    taxonomyType: TaxonomyType.Skill,
  });
  const activityTaxonomyConfig = useActivityTaxonomyConfig();
  const { getReadableValue } = useReportFilters();
  const reportParameters = useReportParameters();

  const {
    isOpen: isFilterOpen,
    onOpen: onFilterOpen,
    onClose: onFilterClose,
  } = useDisclosure();
  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure();
  const [activeParamId, setActiveParamId] = useState<string | null>(null);
  const [editMode, setEditMode] = useState<'add' | 'edit'>('edit');
  const [draftParam, setDraftParam] = useState<ReportParameterConfig | null>(
    null
  );

  const chartParameters = (() => {
    const foundParameters = parameters
      .map((parameter) => {
        return (
          reportParameters.find(
            (p) => p.parameterId === parameter.parameterId
          ) ?? null
        );
      })
      .filter((p) => p !== null);

    return foundParameters;
  })();

  useAddParameterFilterGroup({ parameters: chartParameters });

  const editingParameter =
    chartParameters.find((p) => p.parameterId === activeParamId) || null;

  const filterForModal: Filter | null = (() => {
    if (!editingParameter) return null;
    const filterMapToUse = editingParameter.isPrimary
      ? activeFilterMap
      : filterState.groupMap.get(editingParameter.parameterId);
    if (!filterMapToUse) return null;

    const current =
      filterMapToUse.get(editingParameter.filterId) ||
      getFilterDefinition(editingParameter.filterId);
    return current
      ? { ...current, isPrimary: editingParameter.isPrimary }
      : null;
  })();

  const isParameterSelected = useCallback(
    (parameter: ReportParameterConfig): boolean => {
      const filterMapToUse = parameter.isPrimary
        ? activeFilterMap
        : filterState.groupMap.get(parameter.parameterId);
      if (!filterMapToUse) return false;

      const filter = filterMapToUse.get(parameter.filterId);
      if (!filter) return false;

      if (filter.type === FilterType.TREE) {
        const tree = filter;
        if (parameter.isPrimary) {
          return (tree.primarySelections || []).length > 0;
        }
        return (tree.selections || []).length > 0;
      }

      if (
        filter.type === FilterType.LIST ||
        filter.type === FilterType.STATIC_LIST
      ) {
        const list = filter;
        return (list.selections || []).length > 0;
      }

      if (filter.type === FilterType.VALUE) {
        const val = filter.value;
        return val !== null && val !== undefined && String(val).length > 0;
      }

      return true;
    },
    [filterState, activeFilterMap]
  );

  const openFilterEditor = (parameterId: string) => {
    setActiveParamId(parameterId);
    onFilterOpen();
  };

  const closeFilterEditor = () => {
    onFilterClose();
  };

  const chartParamIds = new Set(parameters.map((p) => p.parameterId));
  const availableParametersToAdd = reportParameters.filter(
    (p) => !chartParamIds.has(p.parameterId)
  );
  const maxParameters =
    reportParameters.length > 0 ? reportParameters.length : 2;
  const canAddParameter =
    chartParameters.length < maxParameters &&
    availableParametersToAdd.length > 0;
  const hasMultipleChoices = availableParametersToAdd.length > 1;

  const addParameterById = (parameterId: string) => {
    dispatch({
      type: 'ADD_ITEM_PLOT_PARAMETER',
      payload: { id: item.id, parameterId },
    });
  };

  const handleAddParameter = () => {
    // Single choice: add the one available parameter
    const singleParam = availableParametersToAdd[0];
    if (singleParam) {
      addParameterById(singleParam.parameterId);
      return;
    }

    const parameterId = crypto.randomUUID();

    dispatch({
      type: 'ADD_REPORT_PARAMETER',
      payload: {
        parameter: {
          parameterId,
          filterId: primaryFilter.id,
          isPrimary: false,
          name:
            primaryFilter.id === FilterId.COMPANY
              ? 'Competitors'
              : `Comparison ${toReadableFilterName(primaryFilter.id)} Group`,
          selectionLimit: 1,
        },
      },
    });

    dispatch({
      type: 'ADD_ITEM_PLOT_PARAMETER',
      payload: { id: item.id, parameterId },
    });

    const newFilterMap = createNonPrimaryParameterFilterMap({
      roleTaxonomyVersion,
      skillTaxonomyVersion,
      ...activityTaxonomyConfig,
    });

    filterDispatch({
      type: 'ADD_NEW_FILTER_GROUP',
      payload: { newFilterKey: parameterId, newFilterMap },
    });
  };

  const handleRemoveParameterFromChart = (parameterId: string) => {
    // Only remove from chart item, not from report parameters
    dispatch({
      type: 'REMOVE_ITEM_PLOT_PARAMETER',
      payload: { id: item.id, parameterId },
    });
  };

  const openEdit = (param: ReportParameterConfig) => {
    setEditMode('edit');
    setDraftParam(param);
    onEditOpen();
  };

  const handleParameterSubmit = (param: ReportParameterConfig) => {
    dispatch({
      type: 'UPDATE_REPORT_PARAMETER',
      payload: {
        parameterId: param.parameterId,
        updates: {
          name: param.name,
          filterId: param.filterId,
          isPrimary: param.isPrimary,
          selectionLimit: param.selectionLimit,
        },
      },
    });
  };

  const handleGroupingChange = (parameterId: string, grouped: boolean) => {
    dispatch({
      type: 'UPDATE_ITEM_PARAMETER_GROUPING',
      payload: { id: item.id, parameterId, grouped },
    });
  };

  return (
    <div className={styles.selector}>
      <div className={styles.header}>
        <p
          className={classNames(
            labelStyles.sectionLabel,
            labelStyles.sectionTitle
          )}
        >
          Parameters
        </p>
        {hasMultipleChoices ? (
          <Menu>
            <MenuButton>
              <IconButton
                variant="ghost"
                size="sm"
                shape="rounded"
                aria-label="add-parameter"
                disabled={chartParameters.length >= maxParameters}
                icon={<IconPlusSmall />}
              />
            </MenuButton>
            <MenuList align="end">
              {availableParametersToAdd.map((param) => (
                <MenuItem
                  key={param.parameterId}
                  onClick={() => addParameterById(param.parameterId)}
                >
                  {param.name}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        ) : (
          <IconButton
            variant="ghost"
            size="sm"
            shape="rounded"
            aria-label="add-parameter"
            disabled={!canAddParameter}
            icon={<IconPlusSmall />}
            onClick={handleAddParameter}
          />
        )}
      </div>
      <div className={styles.paramList}>
        {chartParameters.map((parameter) => {
          return (
            <CardListSelect<string>
              key={parameter.parameterId}
              id={parameter.parameterId}
              label={parameter.name}
              detail={
                getReadableValue({
                  ...(!parameter.isPrimary && {
                    parameterKey: parameter.parameterId,
                  }),
                  filterId: parameter.filterId,
                }) || 'None Selected'
              }
              variant="report"
              isChecked={isParameterSelected(parameter)}
              onChange={() => openFilterEditor(parameter.parameterId)}
              cta={
                <ParamItemMenu>
                  <MenuItem onClick={() => openEdit(parameter)}>Edit</MenuItem>
                  <MenuItem
                    destructive
                    onClick={() =>
                      handleRemoveParameterFromChart(parameter.parameterId)
                    }
                  >
                    Remove
                  </MenuItem>
                </ParamItemMenu>
              }
            />
          );
        })}
      </div>

      {editingParameter && (
        <FilterModal
          isOpen={isFilterOpen}
          onClose={closeFilterEditor}
          title={editingParameter.name}
          filter={filterForModal}
          selectedFilterId={editingParameter.filterId}
          isCentered
          showSelectedValues
          selectionLimit={editingParameter.selectionLimit ?? 1}
          {...(!editingParameter.isPrimary && {
            groupKeyOverride: editingParameter.parameterId,
          })}
          {...(editingParameter.filterId === FilterId.COMPANY && {
            disabledLevels: [0, 1],
          })}
        />
      )}

      {draftParam && (
        <EditParameterModal
          key={`${item.id}-${draftParam.parameterId}`}
          isOpen={isEditOpen}
          onClose={onEditClose}
          mode={editMode}
          initial={draftParam}
          onSubmit={handleParameterSubmit}
          isChartLevel={true}
          plotParameter={item.plot?.plotParameters?.find(
            (p) => p.parameterId === draftParam.parameterId
          )}
          onGroupingChange={(grouped) =>
            handleGroupingChange(draftParam.parameterId, grouped)
          }
        />
      )}
    </div>
  );
};
